import { DayProperties } from '../../../pages/Calendar/Helpers/interfaces';
import { RoleProperties, RoleSectionProperties } from '../identityAPIProperties';
import { MBHBillingStatus } from './billingProperties';
import { PromptCodeProperties } from './dataCollectionProperties';

export interface CreateEventProperties {
	clientId: string;
	date: string;
	endTime: string;
	expirationDate?: string;
	recurringScheduleId?: string | null;
	recurringScheduleTimeSlotId?: string | null;
	sessionTypeId: number;
	startTime: string;
}

export interface AdminCreateEventProperties extends CreateEventProperties {
	isAdminForceOperation?: boolean;
	userId: string;
}

export interface EventProperties {
	endDate: string | null;
	includeBCBAEvents?: boolean;
	includeBTEvents?: boolean;
	startDate: string | null;
}

export interface GetUserEventsProperties {
	endDate: string | null;
	startDate: string | null;
	userId: string;
}

export interface GetClientEventsProperties extends EventProperties {
	clientId: string;
}

export interface DeleteUserEventProperties {
	eventId: string;
	userId: string;
}

export interface DeleteUpcomingEventsProperties {
	clientId: string;
	startDate: string;
}

export interface DeleteEventsProperties {
	endDate: string;
	startDate: string;
}

export interface CopyEventsProperties extends DeleteEventsProperties {
	toDate: string;
}

export interface UserBriefInfoProperties {
	email: string;
	firstName: string;
	fullName: string;
	id: string;
	lastName: string;
	role: RoleProperties;
}

interface ClientProperties {
	dateOfBirthday: string;
	defaultBcba: UserBriefInfoProperties;
	email: string;
	fullName: string;
	gender: string;
	id: string;
	isElc?: boolean;
	timeZoneId?: string;
}

export interface SessionTypeProperties {
	allowedRoleSections: RoleSectionProperties[];
	id: number;
	name: string;
	type: number;
	funderId: string | null;
	// Whether sessions of this type produce a SOAP note / Report. Gate SOAP-note UI on this capability,
	// not on a specific funder. Data-collection-only types (e.g. ELC) are false; clinical types are true.
	requiresSoapNote: boolean;
}

export interface EventDataProperties extends SessionProperties {
	cancellationGroupId?: string | null;
	cancellationId?: string | null;
	cancellationNotes?: string | null;
	cancellationReasonName?: string | null;
	hasPendingCancellation?: boolean;
	isCancelled?: boolean;
	isCancelledApproved?: boolean;
	isRecurringScheduleSession?: boolean;
	mbhBillingStatus?: MBHBillingStatus | null;
	recurringScheduleId?: string;
	recurringScheduleTimeSlotId?: string | null;
	reportId: string | null;
	reportStatus: ReportStatusProperties | null;
}

export interface CalendarEventBase {
	date: string;
	endTime: string;
	id: string;
	startTime: string;
	title: string;
	type: string;
	user: UserBriefInfoProperties;
}

export interface SessionProperties extends CalendarEventBase {
	client: ClientProperties;
	isCompleted: boolean;
	isSessionFlagged: boolean;
	sessionId: string;
	sessionType: SessionTypeProperties;
}

export interface ReportStatusProperties {
	name: string;
	status: number;
}

export interface GetSessionProperties extends SessionProperties {
	reportId: string;
	reportStatus: ReportStatusProperties;
}

interface PromptsResponseProperties {
	date: string;
	id: string;
	promptCode: PromptCodeProperties;
	user: UserBriefInfoProperties;
}

export interface DTTPromptsResponseProperties extends PromptsResponseProperties {
	percentage: number;
}

export interface TAPromptsResponseProperties extends PromptsResponseProperties {
	correct: boolean;
	step: {
		id: string;
		name: string;
		targetId: string;
	};
}

export interface DiscretionaryEventDataProperties extends CalendarEventBase {
	clientId?: string | null;
	clientFullName?: string | null;
	serviceCodeId?: string | null;
	serviceCodeName?: string | null;
	branchId: string | null;
	description: string;
	notes: string | null;
	type: 'DiscretionaryHoursLog';
}

export interface RecurringScheduleSessionProperties {
	branchId: string | null;
	clientId: string;
	clientName: string;
	clientTimeZoneId?: string;
	daysOfWeek: number[];
	isElc?: boolean;
	endDate: string | null;
	endTimeMinutes: number;
	frequency: number;
	id: string;
	isRecurringScheduleSession: true;
	providerId: string;
	providerName: string;
	recurringScheduleTimeSlotId?: string | null;
	sessionType: number;
	sessionTypeId: number;
	sessionTypeName: string;
	startDate: string;
	startTimeMinutes: number;
	step: number;
	type: 'RecurringSchedule';
	weekStartsOn: number;
}

export type CalendarEventItem = EventDataProperties | DiscretionaryEventDataProperties;

export const isDiscretionaryEvent = (event: CalendarEventItem): event is DiscretionaryEventDataProperties =>
	event.type === 'DiscretionaryHoursLog';

export const isRecurringScheduleSession = (event: unknown): event is RecurringScheduleSessionProperties =>
	Boolean(event && typeof event === 'object' && (event as RecurringScheduleSessionProperties).isRecurringScheduleSession);

export interface GetEventsProperties {
	clientId?: string | null | undefined;
	days: DayProperties[];
	includeBCBAEvents?: boolean;
	includeBTEvents?: boolean;
	userId?: string | null | undefined;
}

export interface CreateDiscretionaryHoursProperties {
	clientId?: string | null;
	serviceCodeId?: string | null;
	branchId?: string;
	date: string;
	description: string;
	endTime: string;
	notes?: string;
	startTime: string;
}
